import _ from "lodash";
import React, { useState } from "react";
import { connect } from "react-redux";
import { Trans } from "react-i18next";
import { Popover } from "@toddle-edu/react-tiny-popover";
import classNames from "classnames";

import {
  FamilyInviteOutlined,
  TeacherAddOutlined,
  UserCloseOutlined,
} from "@toddle-edu/ds-icons";

import { formatDate } from "Utils/DateTimeHelpers";
import { isIdEqual } from "Utils/idHelpers";
import { useI18n } from "Hooks";
import {
  getDefaultNameSortPreference,
  getUserType,
  getDefaultStudentNameOrderPreference,
} from "modules/CommonSelectors";
import {
  CHANNEL_ACTIVITY_TYPES,
  DATE_FORMATS,
  getFormattedNameForUserType,
  getSubText,
  isMessagingRevempEnabled,
} from "MessagingV2/modules/Utils";

import UserNameAvatar from "AppComponents/UserNameAvatar/UserNameAvatar";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import MessageActivityV2 from "./MessageActivityV2";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";

import classes from "./MessageActivity.scss";

const styles = {
  avatarStyles: { size: "small", randomizeColor: true, shade: "light" },
};

const UserDropdownPopover = ({
  t,
  options,
  currentUserType,
  defaultNameSortPreference,
}) => {
  return (
    <div className={classes.popoverRoot}>
      {_.map(options, option => {
        const { key, label, userList = [] } = option;
        return (
          <div key={key}>
            <div
              className={classNames("text-label-2", classes.popoverListLabel)}
            >
              {label}
            </div>
            <div className={classes.popoverUserList}>
              {_.map(userList, user => {
                const {
                  id,
                  name,
                  value,
                  profileImage,
                  isArchived,
                  type,
                } = user;
                return (
                  <div className={classes.userItem} key={id}>
                    <UserNameAvatar
                      id={value}
                      profileImage={profileImage}
                      name={name}
                      viewMode={"NAME_AND_CUSTOM_SUBTEXT"}
                      shouldDynamicColor={true}
                      isArchived={isArchived}
                      showAvatar={true}
                      avatarStyles={styles.avatarStyles}
                      showArchivedText={true}
                      showTooltip={false}
                      customSubText={getSubText({
                        loggedInUserRole: currentUserType,
                        messagedUserObj: user,
                        messagedUserRole: type,
                        t,
                        defaultStudentNameSortPreference: defaultNameSortPreference,
                      })}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const userDropdownMapStateToProps = state => ({
  defaultNameSortPreference: getDefaultNameSortPreference(state),
  currentUserType: getUserType(state),
});

const UserDropdownPopoverContent = connect(userDropdownMapStateToProps)(
  UserDropdownPopover
);

const UsersDropdown = ({ options, children, t }) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const closePopover = () => setPopoverOpen(false);
  const openPopover = () => setPopoverOpen(true);

  const handleOpenPopoverKeyDown = e => onEnterOrSpaceKeyDown(e, openPopover);

  return (
    <Popover
      isOpen={popoverOpen}
      onClickOutside={closePopover}
      containerClassName={classes.popoverContainer}
      positions={["right", "left", "top", "bottom"]} // preferred positions by priority
      content={<UserDropdownPopoverContent options={options} t={t} />}
    >
      <div
        className={classNames("text-label-3", classes.link)}
        onClick={openPopover}
        role="button"
        tabIndex={0}
        onKeyDown={handleOpenPopoverKeyDown}
      >
        {children}
      </div>
    </Popover>
  );
};

const getUserListItems = ({
  users,
  defaultNameSortPreference,
  defaultStudentNameOrderPreference,
}) => {
  return _.map(users, user => {
    const fullName = getFormattedNameForUserType({
      user: user,
      orderBy: defaultNameSortPreference,
      defaultStudentNameOrderPreference,
      isFormattedNameV2: true,
    });

    const { id } = user;
    return {
      key: id,
      name: fullName,
      ...user,
    };
  });
};

const getUserListDropdownOptions = ({
  users,
  defaultNameSortPreference,
  defaultStudentNameOrderPreference,
  t,
}) => {
  const userGroupBy = _.groupBy(users, "type");

  const targetUserOptions = [
    {
      key: "staff_list",
      label: t("common:staff_member"),
      userList: getUserListItems({
        users: _.get(userGroupBy, "staff", []),
        defaultNameSortPreference,
      }),
    },
    {
      key: "student_list",
      label: t("common:student"),
      userList: getUserListItems({
        users: _.get(userGroupBy, "student", []),
        defaultNameSortPreference,
        defaultStudentNameOrderPreference,
      }),
    },
    {
      key: "family_list",
      label: t("common:family_member"),
      userList: getUserListItems({
        users: _.get(userGroupBy, "parent", []),
        defaultNameSortPreference,
      }),
    },
  ];

  return _.filter(targetUserOptions, option => !_.isEmpty(option.userList));
};

const MessageActivity = ({
  message,
  defaultNameSortPreference,
  defaultStudentNameOrderPreference,
}) => {
  const { activeUserId: currentUserId } = useChatOptions();

  const { t } = useI18n(["common"]);
  const activity = _.get(message, "channelActivity", {});
  const createdBy = _.get(message, "createdBy", {});
  const { type, after, before, targetUsers } = activity;

  const targetUserCount = _.size(targetUsers);
  const isSingleTargetUser = targetUserCount === 1;

  const targetUserOptions =
    targetUserCount > 0 &&
    getUserListDropdownOptions({
      users: targetUsers,
      defaultNameSortPreference,
      defaultStudentNameOrderPreference,
      t,
    });

  const singleTargetUserName =
    isSingleTargetUser &&
    getFormattedNameForUserType({
      user: _.first(targetUsers),
      orderBy: defaultNameSortPreference,
      defaultStudentNameOrderPreference,
      isFormattedNameV2: true,
    });

  const isCurrentUserTargetUser =
    isSingleTargetUser &&
    isIdEqual(currentUserId, _.get(_.first(targetUsers), "id"));

  const isCreatedByCurrentUser = isIdEqual(
    currentUserId,
    _.get(createdBy, "id")
  );

  const createdByName = isCreatedByCurrentUser
    ? t("common:you")
    : getFormattedNameForUserType({
        user: createdBy,
        orderBy: defaultNameSortPreference,
        defaultStudentNameOrderPreference,
        isFormattedNameV2: true,
      });

  switch (type) {
    case CHANNEL_ACTIVITY_TYPES.DESCRIPTION_CHANGE:
      return (
        <div className={classNames("text-body-2", classes.activityContainer)}>
          <FamilyInviteOutlined
            variant="subtle"
            className={classes.activityContainerIcon}
          />
          <Trans i18nKey="common:channel_activity_description_updated">
            <span className={classes.textBold}>{{ description: after }}</span>
          </Trans>
        </div>
      );
    case CHANNEL_ACTIVITY_TYPES.LABEL_CHANGE:
      return (
        <div className={classNames("text-body-2", classes.activityContainer)}>
          <FamilyInviteOutlined
            variant="subtle"
            className={classes.activityContainerIcon}
          />
          <Trans i18nKey="common:channel_activity_label_updated">
            <span className={classes.textBold}>{{ before }}</span>
            <span className={classes.textBold}> {{ after }}</span>
          </Trans>
        </div>
      );
    case CHANNEL_ACTIVITY_TYPES.USER_ADD: {
      if (
        isSingleTargetUser &&
        isIdEqual(_.get(_.first(targetUsers), "id"), _.get(createdBy, "id"))
      ) {
        return (
          <div className={classNames("text-body-2", classes.activityContainer)}>
            <TeacherAddOutlined
              variant="subtle"
              className={classes.activityContainerIcon}
            />
            <Trans i18nKey="common:channel_activity_user_joined_this_channel">
              {isCurrentUserTargetUser ? (
                <span> {{ name: t("common:you") }}</span>
              ) : (
                <UsersDropdown t={t} options={targetUserOptions}>
                  {{ name: singleTargetUserName }}
                </UsersDropdown>
              )}
            </Trans>
          </div>
        );
      }

      return (
        <div className={classNames("text-body-2", classes.activityContainer)}>
          <TeacherAddOutlined
            variant="subtle"
            className={classes.activityContainerIcon}
          />
          {isSingleTargetUser ? (
            <Trans i18nKey="common:channel_activity_user_added_msg_singular">
              <span>{{ name: createdByName }}</span>
              {isCurrentUserTargetUser ? (
                <span> {{ label: t("common:you") }}</span>
              ) : (
                <UsersDropdown t={t} options={targetUserOptions}>
                  {{ label: singleTargetUserName }}
                </UsersDropdown>
              )}
            </Trans>
          ) : (
            <Trans i18nKey="common:channel_activity_user_added_msg">
              <span>{{ name: createdByName }}</span>
              <UsersDropdown t={t} options={targetUserOptions}>
                {{ membersCount: targetUserCount }}
              </UsersDropdown>
            </Trans>
          )}
        </div>
      );
    }
    case CHANNEL_ACTIVITY_TYPES.USER_REMOVE:
      return (
        <div className={classNames("text-body-2", classes.activityContainer)}>
          <UserCloseOutlined
            variant="subtle"
            className={classes.activityContainerIcon}
          />
          {isSingleTargetUser ? (
            <Trans i18nKey="common:channel_activity_user_removed_msg_singular">
              <span>{{ name: createdByName }}</span>
              {isCurrentUserTargetUser ? (
                <span> {{ label: t("common:you") }}</span>
              ) : (
                <UsersDropdown t={t} options={targetUserOptions}>
                  {{ label: singleTargetUserName }}
                </UsersDropdown>
              )}
            </Trans>
          ) : (
            <Trans i18nKey="common:channel_activity_user_removed_msg">
              <span>{{ name: createdByName }}</span>
              <UsersDropdown t={t} options={targetUserOptions}>
                {{ membersCount: targetUserCount }}
              </UsersDropdown>
            </Trans>
          )}
        </div>
      );
    default:
      return null;
  }
};

const mapStateToProps = state => ({
  defaultNameSortPreference: getDefaultNameSortPreference(state),
  defaultStudentNameOrderPreference: getDefaultStudentNameOrderPreference(
    state
  ),
});

const MessageActivityContainer = connect(mapStateToProps)(MessageActivity);

const MessageActivityWithDate = props => {
  const createdAt = _.get(props, "message.createdAt");
  return (
    <div className={classes.messageActivity}>
      <MessageActivityContainer {...props} />
      <div className="text-body-3">
        {formatDate({
          date: createdAt,
          format: DATE_FORMATS.SENT_MESSAGE,
        })}
      </div>
    </div>
  );
};

const MessageActivityWrapper = props => {
  return isMessagingRevempEnabled() ? (
    <MessageActivityV2 {...props} />
  ) : (
    <MessageActivityWithDate {...props} />
  );
};

export default MessageActivityWrapper;
